import React, { useState } from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import CircularProgress from '@mui/material/CircularProgress'
import SvgIcon from '@mui/material/SvgIcon'
import { iconsMap } from '@tdcerhverv/parrotfish/dist/icons/icons'
import Expand_down from '@tdcerhverv/parrotfish/dist/icons/Icons/Expand_down.svg'
import { pageStatuses, outageStatuses } from './Enums'
import { IOutageInfo, UpdateBox } from './types'
import { Box } from '@mui/material'

type UpdateHistory = {
  date: string
  end?: boolean
  details?: string
  status: string
  updates?: boolean
}

const { LOADING, RESULTS } = pageStatuses
const { CLOSED, ACTIVE, PLANNED } = outageStatuses

const tabs = [
  { label: 'Aktuelle', status: ACTIVE },
  { label: 'Planlagte', status: PLANNED },
  { label: 'Afsluttede', status: CLOSED },
]

const months: { [key: string]: string } = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'Maj',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Okt',
  '11': 'Nov',
  '12': 'Dec',
}

const outageStatusIcons: { [k: string]: string } = {
  ACTIVE: 'Error',
  CLOSED: 'Checkmark_round_light',
  PLANNED: 'Warning',
}
const outageTitles = {
  ACTIVE: 'Driftsforstyrrelse registreret',
  CLOSED: 'Driftsforstyrrelse registreret',
  PLANNED: 'Driftsforstyrrelse planlagt',
}

const outageEndTimeTexts = {
  ACTIVE: 'Der arbejdes på at driftforstyrrelsen bliver udbedret og det forventes løst den',
  CLOSED: 'Problemet blev løst den',
  PLANNED: 'Driftsforstyrrelsen forventes overstået den',
}

const OutageInfo = ({ content, status, outages }: IOutageInfo) => {
  const { palette, breakpoints } = useTheme()
  const styles = {
    wrapper: {
      marginBottom: '50px',
      '& > a > h6': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    antennaIcon: {
      fontSize: '22px',
      marginRight: '16px',
      verticalAlign: 'middle',
    },
    notificationBox: {
      marginTop: '30px',
      marginLeft: '10px',
      marginBottom: '40px',
      cursor: 'pointer',
      color: palette.grey['200'],
      textDecoration: 'none',
    },
    spinner: {
      color: palette.common.black,
    },
    spinnerContainer: {
      textAlign: 'center',
      marginTop: '20px',
      marginBottom: '20px',
    },
    tabs: {
      '& .MuiTabs-flexContainer': {
        borderBottom: 'none',
      },
      marginTop: '30px',
      marginBottom: '20px',
    },
    accordion: {
      marginTop: '10px',
    },
    expandIcon: {
      fontSize: '14px',
    },
    generalStatusIcon: {
      fontSize: '24px',
      marginRight: '15px',
      verticalAlign: 'middle',
    },
    successColor: {
      color: palette.success.main,
    },
    errorColor: {
      color: palette.error.main,
    },
    accordionDetails: {
      display: 'block',
    },
    updates: {
      marginTop: '20px',
      marginBottom: '20px',
    },
    updateBox: {
      position: 'relative',
      marginTop: '20px',
      marginBottom: '20px',
      marginLeft: '35px',
    },
    opaque: {
      opacity: '0.5',
    },
    updateTimestamp: {
      position: 'absolute',
      top: '2px',
      right: '5px',
      [breakpoints.down('md')]: {
        position: 'unset',
        marginBottom: '12px',
      },
    },
    updateTitle: {
      marginBottom: '12px',
      [breakpoints.down('md')]: {
        marginBottom: 0,
      },
    },
    updateBall: {
      backgroundColor: palette.grey['800'],
      borderRadius: '50%',
      width: '10px',
      height: '10px',
      position: 'absolute',
      left: '-30px',
      top: '3px',
    },
    updateText: {
      whiteSpace: 'pre-line',
    },
    lastUpdateBall: {
      backgroundColor: 'blue',
    },
    updateLine: {
      position: 'absolute',
      width: 0,
      borderLeft: `2px solid ${palette.grey['800']}`,
      left: '-26px',
      top: '14px',
      bottom: '-22px',
    },
    lastUpdateLine: {
      borderLeft: `2px dashed ${palette.grey['800']}`,
    },
    noOutagesText: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: 'calc(100% - 50px)',
    },
    noOutagesTextWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    noOutagesLink: {
      color: palette.common.white,
      marginLeft: '4px',
    },
  }
  const [tab, setTab] = useState(0)

  const getOutagesByStatus = (status: string) => {
    return outages ? outages.filter(issue => issue.issueStatus === status) : []
  }

  const UpdateBox = ({ i, updateTime, updateText, isLast, status }: UpdateBox) => {
    //const classes = useStyles({ isLast: isLast })
    return (
      <Box sx={[styles.updateBox, (!isLast || (isLast && status === 'CLOSED')) && styles.opaque]}>
        <Typography sx={styles.updateTitle} component="h5" variant="subtitle1">
          {`${i + 1}. Opdatering`}
        </Typography>
        <Typography sx={styles.updateTimestamp} variant="body1">
          {formatDate(updateTime)}
        </Typography>
        {/* Null check because sometimes test data is null */}
        {updateText && (
          <Typography variant="body1" sx={styles.updateText}>
            {updateText.replace('YouSee', 'TDC Erhverv')}
          </Typography>
        )}
        <Box sx={[styles.updateBall, isLast && status !== 'CLOSED' && styles.lastUpdateBall]} />
        <Box sx={[styles.updateLine, isLast && status !== 'CLOSED' && styles.lastUpdateLine]} />
      </Box>
    )
  }

  const getUpdateTitle = (end: boolean | undefined, status: string) => {
    if (!end) {
      return status === 'PLANNED' ? 'Påbegyndes' : 'Fejlmeldt'
    } else {
      return status === 'CLOSED' ? 'Afsluttet' : 'Forventes afsluttet'
    }
  }

  const StartEndtimeUpdateBox = ({ date, end, details, status, updates = false }: UpdateHistory) => {
    return (
      <Box
        sx={[styles.updateBox, styles.opaque]}
        style={(end && status === 'CLOSED') || (!updates && !end && status !== 'CLOSED') ? { opacity: 1 } : {}}
      >
        <Typography sx={styles.updateTitle} component="h5" variant="subtitle1">
          {getUpdateTitle(end, status)}
        </Typography>
        <Typography sx={styles.updateTimestamp} variant="body2">
          {formatDate(date)}
        </Typography>

        {details && (
          <Typography variant="body2" sx={styles.updateText}>
            {details.replace('YouSee', 'TDC Erhverv')}
          </Typography>
        )}
        {!end && <Box sx={styles.updateLine} />}
        <Box
          sx={[
            styles.updateBall,
            (end && status === 'CLOSED') || (!updates && !end && status !== 'CLOSED')
              ? { ...styles.lastUpdateBall }
              : {},
          ]}
        />
      </Box>
    )
  }

  const getIconByStatus = (status: string) => {
    return (
      <SvgIcon
        component={iconsMap[outageStatusIcons[status] as keyof typeof iconsMap]}
        sx={[styles.generalStatusIcon, status === 'CLOSED' ? styles.successColor : styles.errorColor]}
        viewBox="0 0 48 48"
      />
    )
  }

  const formatDate = (date: string) => {
    if (!date) return ''
    const splitDate = date.split(/[-\s:]+/)
    const day = splitDate[0].charAt(0) === '0' ? splitDate[0].substring(1) : splitDate[0]
    const month = months[splitDate[1]]
    const year = splitDate[2]
    const hour = splitDate[3]
    const minute = splitDate[4]
    return `${day}. ${month} ${year} kl. ${hour}:${minute}`
  }
  const [expanded, setExpanded] = React.useState<string | false>(false)
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  if (status === LOADING) {
    return (
      <Box sx={styles.spinnerContainer}>
        <CircularProgress sx={styles.spinner} />
      </Box>
    )
  }
  return (
    <Box sx={styles.wrapper}>
      {status === RESULTS && (
        <>
          <Tabs
            sx={styles.tabs}
            TabIndicatorProps={{
              style: { color: palette.common.black },
            }}
            value={tab}
            onChange={(e, tab) => setTab(tab)}
          >
            {tabs.map((tab, idx) => (
              <Tab key={idx} label={`${tab.label} (${getOutagesByStatus(tab.status).length})`} />
            ))}
          </Tabs>
          {getOutagesByStatus(tabs[tab].status).length ? (
            getOutagesByStatus(tabs[tab].status).map((outage, idx) => (
              <Accordion
                expanded={expanded === 'panel'}
                onChange={handleChange('panel')}
                variant={'outlined'}
                key={idx}
                sx={styles.accordion}
              >
                <AccordionSummary
                  expandIcon={<SvgIcon component={Expand_down} sx={styles.expandIcon} viewBox="0 0 48 48" />}
                  aria-controls="panel-content"
                  id="panel-header"
                >
                  {getIconByStatus(outage.issueStatus)}
                  <Typography variant="subtitle1">
                    {`${outageTitles[outage.issueStatus]} ${formatDate(outage.startTime)}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={styles.accordionDetails}>
                  <Typography variant="body2">
                    {`${outageEndTimeTexts[outage.issueStatus]} ${formatDate(
                      outage.issueStatus === 'CLOSED' ? outage.endTime : outage.expectedEndTime,
                    )}`}
                  </Typography>

                  <Box sx={styles.updates}>
                    <StartEndtimeUpdateBox
                      date={outage.startTime}
                      details={outage.details}
                      status={outage.issueStatus}
                      updates={outage.updates as unknown as boolean}
                    />

                    {outage.updates
                      ?.reverse()
                      .map((update: any, i: number) => (
                        <UpdateBox
                          key={i + outage.driftInfoId}
                          i={i}
                          isLast={i === outage.updates.length - 1}
                          status={outage.issueStatus}
                          {...update}
                        />
                      ))}

                    <StartEndtimeUpdateBox
                      date={outage.issueStatus === 'CLOSED' ? outage.endTime : outage.expectedEndTime}
                      end
                      status={outage.issueStatus}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Box sx={styles.noOutagesTextWrapper}>
              <Box sx={styles.noOutagesText}>
                <Typography variant="body2">
                  Vi har ikke registreret nogen {tabs[tab].label.toLocaleLowerCase()} driftsforstyrrelser på den
                  indtastede adresse.
                </Typography>
                {tabs[tab].label === 'Aktuelle' && (
                  <Typography variant="body2">
                    Oplever du alligevel fejl på din internetforbindelse? <br />
                    Vores driftsinformation viser udelukkende generelle fejl i området.
                    <br />
                    Det vil sige, at hvis du oplever et nedbrud på din forbindelse, er det ikke nødvendigvis tilknyttet
                    et størrer nedbrud. <br /> <br />
                    Hvis du har fejlmeldt din forbindelse, arbejder vi selvfølgelig på at løse fejlen.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default OutageInfo
